import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { IconBuilder } from '@components/icons/builder';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Shimmer } from '@components/shimmer';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { seconds } from '@utils/milliseconds';

type LoadingStateDisplayProps = {
  isGenerating: boolean;
};

const ROTATING_KEYS = ['working', 'thinking', 'processing', 'cooking'] as const;
const ROTATING_INTERVAL = seconds(15);
const TEXT_TRANSITION_MS = 160;

export const LoadingStateDisplay = ({ isGenerating }: LoadingStateDisplayProps) => {
  const { t } = useScopedTranslate(locale);
  const [textIndex, setTextIndex] = useState(0);
  const [displayedKey, setDisplayedKey] = useState<(typeof ROTATING_KEYS)[number]>(
    ROTATING_KEYS[0]
  );
  const [isTextVisible, setIsTextVisible] = useState(true);

  useEffect(() => {
    if (!isGenerating) return;
    setTextIndex(0);
    const interval = setInterval(() => {
      setTextIndex((i) => (i + 1) % ROTATING_KEYS.length);
    }, ROTATING_INTERVAL);
    return () => clearInterval(interval);
  }, [isGenerating]);

  useEffect(() => {
    if (!isGenerating) {
      setDisplayedKey(ROTATING_KEYS[0]);
      setIsTextVisible(true);
      return;
    }

    const nextKey = ROTATING_KEYS[textIndex];
    if (nextKey === displayedKey) {
      setIsTextVisible(true);
      return;
    }

    setIsTextVisible(false);
    const timeout = setTimeout(() => {
      setDisplayedKey(nextKey);
      setIsTextVisible(true);
    }, TEXT_TRANSITION_MS);

    return () => clearTimeout(timeout);
  }, [displayedKey, isGenerating, textIndex]);

  if (!isGenerating) {
    return null;
  }

  return (
    <StatusRow>
      <AssistantBorder>
        <AssistantMessageContainer>
          <Stack orientation="horizontal" justify="space-between" align="center">
            <Stack orientation="horizontal" justify="center" align="center" space={2}>
              <IconBuilder color="aiGradientTeal100" />
              <TransitioningShimmer
                shimmerSpeed={50}
                shimmerWidth={4}
                aiGradient
                font="semibold12"
                text={t(displayedKey)}
                $isTextVisible={isTextVisible}
              />
            </Stack>
            <AIGradientSpinner size={16} $skipMargin $borderWidth={1.5} />
          </Stack>
        </AssistantMessageContainer>
      </AssistantBorder>
    </StatusRow>
  );
};

const StatusRow = styled.div({
  padding: `${pxToRem(8)} 0`,
});

const AIGradientSpinner = styled(SizedRelativeSpinner)<{ $borderWidth?: number }>(
  ({ $borderWidth = 2 }) => ({
    '&& > div::before': {
      border: 'none',
      padding: pxToRem($borderWidth),
      background: `conic-gradient(
        from -90deg,
        transparent 0deg 120deg,
        ${theme.color.elementTeal100} 210deg,
        ${theme.color.elementViolet100} 360deg
      )`,
      WebkitMask: `radial-gradient(
        farthest-side,
        transparent calc(100% - ${$borderWidth}px),
        #000 calc(100% - ${$borderWidth}px)
      )`,
      mask: `radial-gradient(
        farthest-side,
        transparent calc(100% - ${$borderWidth}px),
        #000 calc(100% - ${$borderWidth}px)
      )`,
    },
  })
);

const AssistantBorder = styled.div({
  width: '100%',
  borderRadius: pxToRem(8),
  padding: pxToRem(1),
  background: theme.gradient.aiSurface30,
});

const AssistantMessageContainer = styled.div({
  width: '100%',
  borderRadius: pxToRem(8),
  padding: pxToRem(12),
  background: theme.color.backgroundLightDark,
});

const TransitioningShimmer = styled(Shimmer)<{ $isTextVisible: boolean }>(({ $isTextVisible }) => ({
  display: 'inline-block',
  opacity: $isTextVisible ? 1 : 0,
  transform: `translateY(${!$isTextVisible ? pxToRem(2) : 0})`,
  transition: `opacity ${TEXT_TRANSITION_MS}ms ease, transform ${TEXT_TRANSITION_MS}ms ease`,
  '@media (prefers-reduced-motion: reduce)': {
    opacity: 1,
    transform: 'none',
    transition: 'none',
  },
}));

const locale = {
  en: {
    working: 'Working...',
    thinking: 'Thinking...',
    processing: 'Processing...',
    cooking: 'Cooking...',
  },
};
